/**
 * OCTOPUS - LiveTab (item 6.17)
 * Aba "Ao vivo" do monitor de equipamento: mapa AO VIVO (mesmo engine e
 * comportamento do mapa do Dashboard — FleetMap: seguir a embarcação,
 * atualização por socket, trilha com presets cumulativos, camadas, zoom) +
 * seções dinâmicas montadas 100% pelo CADASTRO do equipamento
 * (grupos do GET /equipments/{id}/live-view).
 *
 * Padrão do mapa copiado do OperatorDashboardPage (kiosk single-vessel):
 * - follow = Set com 1 id no FollowController; arrastar o mapa desengata;
 * - posição via socket equipment_update (heading/cog/speed_knots já chegam
 *   convertidos no broadcast — fix CC-2026-05-29-001; INV-UI-TRAIL-001);
 * - trilha pelo MESMO endpoint do dashboard (/dashboard/equipment/{id}/trail,
 *   presets cumulativos 1/4/12/24/48/72h — INV-TRAIL-002), refresh 60s com
 *   trava de concorrência (não é socket-driven; evita a tempestade de
 *   requests da RCA do kiosk) e SEM cap de pontos (INV-TRAIL-001/004).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { MapPin, Loader2, Activity, Navigation, Crosshair } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import FleetMap from '@/components/map/FleetMap';
import SectionPanel from './SectionPanel';
import WorkHoldBadge from './WorkHoldBadge';

const API = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = '/api/socket.io';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TRAIL_PRESETS = ['1', '4', '12', '24', '48', '72'];

const LiveTab = ({
  equipmentId,
  equipment,
  liveView,
  loadingLive = false,
  geofences = [],
  refreshNonce = 0,
}) => {
  const navigate = useNavigate();
  const [trailHours, setTrailHours] = useState('24'); // mesmo default do dashboard
  const [trail, setTrail] = useState({ points: [] });
  const [followLocked, setFollowLocked] = useState(true);
  // Campos de exibição do marcador (lat/lon/heading/cog/speed) — seed pelo
  // map-data (mesma fonte do Dashboard) e atualização ao vivo pelo socket.
  const [liveNav, setLiveNav] = useState(null);
  // RC-2: estado de atividade do worker (lab_activity_state) — usado SÓ para o
  // chip de histerese (work_hold). Seed por REST; atualização ao vivo pelo
  // socket lab_activity_update (mesma room "dashboard" já usada abaixo).
  const [labActivity, setLabActivity] = useState(null);
  const socketRef = useRef(null);
  const trailInFlightRef = useRef(false);

  // Seed inicial do marcador: /dashboard/map-data computa heading/cog/speed
  // no backend (INV-UI-TRAIL-001) — mesma fonte do mapa do Dashboard.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/dashboard/map-data`, { headers: getAuthHeaders() });
        const list = res.data?.equipments || res.data || [];
        const mine = Array.isArray(list) ? list.find(e => e.id === equipmentId) : null;
        if (active && mine) setLiveNav(prev => ({ ...mine, ...(prev || {}) }));
      } catch (err) {
        // não-crítico: o socket preenche na primeira mensagem
      }
    })();
    return () => { active = false; };
  }, [equipmentId]);

  // Seed + refresh do estado de atividade (work_hold) — endpoint read-only do
  // worker. Polling de 30s (o socket só emite em TRANSIÇÃO de etapa; o hold
  // corre entre transições e chega ao doc pelo flush de 30s do worker).
  useEffect(() => {
    let active = true;
    setLabActivity(null);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/lab/activity/${equipmentId}`, { headers: getAuthHeaders() });
        if (active) setLabActivity(res.data || null);
      } catch (err) {
        // não-crítico: equipamento sem máquina de atividade fica sem chip
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, [equipmentId]);

  // Posição ao vivo via socket (mesmo evento do Dashboard).
  useEffect(() => {
    const token = localStorage.getItem('token');
    socketRef.current = io(API, {
      path: SOCKET_PATH,
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
    });
    socketRef.current.on('connect', () => {
      socketRef.current.emit('join_room', { room_id: 'dashboard' });
    });
    socketRef.current.on('lab_activity_update', (data) => {
      if (data?.equipment_id !== equipmentId) return;
      setLabActivity(data);
    });
    socketRef.current.on('equipment_update', (data) => {
      if (data?.id !== equipmentId) return;
      setLiveNav(prev => ({
        ...(prev || {}),
        latitude: data.latitude != null ? data.latitude : prev?.latitude,
        longitude: data.longitude != null ? data.longitude : prev?.longitude,
        status: data.status || prev?.status,
        // já convertidos no broadcast; só atualiza quando presentes para não
        // apagar o último valor conhecido (fix CC-2026-05-29-001)
        heading: data.heading != null ? data.heading : prev?.heading,
        cog: data.cog != null ? data.cog : prev?.cog,
        speed_knots: data.speed_knots != null ? data.speed_knots : prev?.speed_knots,
        marker_color: data.marker_color || prev?.marker_color,
      }));
    });
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [equipmentId]);

  // Trilha: mesmo endpoint/presets do Dashboard; refresh lento com trava.
  const loadTrail = useCallback(async () => {
    if (!equipmentId || trailInFlightRef.current) return;
    void refreshNonce; // dep usada para forçar re-fetch no botão "Atualizar"
    trailInFlightRef.current = true;
    try {
      const res = await axios.get(
        `${API}/api/dashboard/equipment/${equipmentId}/trail`,
        { headers: getAuthHeaders(), params: { hours: parseInt(trailHours, 10) } }
      );
      setTrail({ points: res.data?.points || [] });
    } catch (err) {
      // trilha é não-crítica; preserva a anterior em caso de falha
    } finally {
      trailInFlightRef.current = false;
    }
  }, [equipmentId, trailHours, refreshNonce]);

  useEffect(() => {
    loadTrail();
    const interval = setInterval(loadTrail, 60000);
    return () => clearInterval(interval);
  }, [loadTrail]);

  const latitude = liveNav?.latitude ?? equipment?.latitude;
  const longitude = liveNav?.longitude ?? equipment?.longitude;
  const position = latitude != null && longitude != null ? [latitude, longitude] : null;

  const focusEquipments = useMemo(() => {
    if (position == null) return [];
    return [{
      id: equipmentId,
      name: equipment?.name,
      tag: equipment?.tag,
      latitude,
      longitude,
      heading: liveNav?.heading,
      cog: liveNav?.cog,
      speed_knots: liveNav?.speed_knots,
      marker_color: liveNav?.marker_color
        || ((liveNav?.status || equipment?.status) === 'online' ? 'green' : 'gray'),
      status: liveNav?.status || equipment?.status,
      equipment_type_name: equipment?.equipment_type_name,
    }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, equipment, liveNav, latitude, longitude]);

  const followedIds = useMemo(
    () => (followLocked && equipmentId ? new Set([equipmentId]) : new Set()),
    [followLocked, equipmentId],
  );

  const groups = liveView?.groups || [];
  const navGroup = groups.find(g => g.kind === 'navigation');
  const motorGroups = groups.filter(g => g.kind === 'motor');
  const sectionGroups = groups.filter(g => g.kind === 'section');
  const hasNavigation = Boolean(navGroup) || position != null;

  if (!loadingLive && groups.length === 0) {
    return (
      <Card className="bg-muted/50" data-testid="live-empty-state">
        <CardContent className="p-12 text-center">
          <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhum sinal cadastrado</h3>
          <p className="text-muted-foreground">
            Configure as fontes e o cadastro de telemetria deste equipamento para
            visualizar os dados ao vivo.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(`/equipments/${equipmentId}/sources`)}
          >
            Configurar Fontes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-tab">
      {/* RC-2: histerese de saída em curso — countdown do fechamento retroativo */}
      <WorkHoldBadge workHold={labActivity?.work_hold} />

      {/* Mapa AO VIVO + Navegação (só p/ equipamentos com posição/sensores) */}
      {hasNavigation && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className={navGroup ? 'lg:col-span-3' : 'lg:col-span-5'}>
            <Card className="h-full">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-5 w-5 text-primary" />
                  Posição e trilha
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative h-[400px] lg:h-[500px]">
                  {position ? (
                    <>
                      <FleetMap
                        equipments={focusEquipments}
                        trails={{ [equipmentId]: trail }}
                        geofences={geofences}
                        selectedEquipmentId={equipmentId}
                        followedIds={followedIds}
                        onFollowDisengage={() => setFollowLocked(false)}
                        initialCenter={position}
                        initialZoom={15}
                        defaultLayer="satellite"
                        renderPopup={(eq) => eq.name || eq.tag}
                        className="h-full w-full"
                      />
                      {/* Controles espelhando o dashboard: período + Seguir + Centralizar */}
                      <div className="absolute left-3 top-3 z-[500] flex items-center gap-2">
                        <Select value={trailHours} onValueChange={setTrailHours}>
                          <SelectTrigger className="w-[80px] h-8 text-xs bg-background/90 backdrop-blur" data-testid="live-trail-hours">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRAIL_PRESETS.map(h => (
                              <SelectItem key={h} value={h}>{h}h</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="sm"
                          variant={followLocked ? 'default' : 'secondary'}
                          className={cn('gap-1', followLocked && 'bg-sky-600 hover:bg-sky-700')}
                          onClick={() => setFollowLocked(value => !value)}
                          title={followLocked ? 'Seguindo o equipamento (arraste o mapa para soltar)' : 'Seguir o equipamento'}
                          data-testid="live-follow-toggle"
                        >
                          <Navigation className="h-4 w-4" />
                          {followLocked ? 'Seguindo' : 'Seguir'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="gap-1"
                          onClick={() => setFollowLocked(true)}
                          title="Centralizar no equipamento"
                        >
                          <Crosshair className="h-4 w-4" />
                          Centralizar
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                      <MapPin className="mr-2 h-5 w-5" />
                      Sem posição GPS
                    </div>
                  )}
                </div>
                <div className="p-3 border-t bg-muted/30">
                  {trail.points.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Sem trilha nas últimas {trailHours}h
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {trail.points.length} pontos nas últimas {trailHours}h
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {navGroup && (
            <div className="lg:col-span-2">
              <SectionPanel section={navGroup} equipmentId={equipmentId} />
            </div>
          )}
        </div>
      )}

      {!hasNavigation && loadingLive && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Motores (Propulsão / Draga / Motor) — grupos do cadastro */}
      {motorGroups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="live-motor-groups">
          {motorGroups.map(group => (
            <SectionPanel key={group.key} section={group} equipmentId={equipmentId} />
          ))}
        </div>
      )}

      {/* Demais seções (Tanques, Elétrica, Ambiente, ...) */}
      {sectionGroups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="live-section-groups">
          {sectionGroups.map(group => (
            <SectionPanel key={group.key} section={group} equipmentId={equipmentId} />
          ))}
        </div>
      )}

      {/* Instâncias de motor fora do cadastro (espelhos/fantasmas) não são
          exibidas como dado operacional (INV-LAYER-READPATH-001) — só o aviso. */}
      {(liveView?.unregistered_motors || []).length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="live-unregistered-hint">
          {liveView.unregistered_motors.length} instância(s) de motor detectada(s) no
          barramento fora do cadastro ({liveView.unregistered_motors.join(', ')}) —
          cadastre ou mantenha oculta(s) em Equipamentos → Editar → Telemetria (Ao vivo).
        </p>
      )}
    </div>
  );
};

export default LiveTab;
